import { CheckCircleFilled, EditOutlined, WarningFilled } from '@ant-design/icons'
import { Alert, Button, Card, InputNumber, Modal, Tooltip, message } from 'antd'
import React, { useState } from 'react'
import { eur, num, stepLabel } from './format'

const MONTHS = ['enero', 'febrero', 'marzo', 'abril', 'mayo', 'junio', 'julio', 'agosto', 'septiembre', 'octubre', 'noviembre', 'diciembre']

// 'YYYY-MM' si el periodo es un mes natural completo; si no, null (la factura de Orange es mensual).
export const fullMonthOf = ({ from, to }) => {
  if (!from || !to || from.slice(0, 7) !== to.slice(0, 7) || !from.endsWith('-01')) return null
  const [y, m] = from.split('-').map(Number)
  const last = new Date(Date.UTC(y, m, 0)).getUTCDate()
  return Number(to.slice(8, 10)) === last ? from.slice(0, 7) : null
}

export const monthLabel = (ym) => `${MONTHS[Number(ym.slice(5, 7)) - 1]} ${ym.slice(0, 4)}`

const TOLERANCE_PCT = 5

const signed = (n, fmt = num) => `${n > 0 ? '+' : ''}${fmt(n)}`
const pctOf = (diff, base) => (base ? (diff / base) * 100 : 0)
const pctTxt = (p) => `${p > 0 ? '+' : ''}${p.toFixed(Math.abs(p) < 1 ? 2 : 1).replace('.', ',')} %`

const cell = { padding: '6px 8px', borderBottom: '1px solid #f1f5f9', textAlign: 'right', whiteSpace: 'nowrap' }
const cellL = { ...cell, textAlign: 'left', whiteSpace: 'normal' }

const stepList = (byStep, field) =>
  Object.entries(byStep || {})
    .filter(([, v]) => v[field] > 0)
    .sort((a, b) => b[1][field] - a[1][field])
    .map(([step, v]) => `${num(v[field])} ${step === 'init' ? 'mensajes iniciales sin respuesta en 24 h' : `"${stepLabel(step)}"`}`)
    .join(', ')

const FIELDS = [
  { key: 'conversaciones', label: 'Sesiones (a2p_conversation)', step: 1 },
  { key: 'simple', label: 'Mensajes a 0,05 € (basic_message 0,05)', step: 1 },
  { key: 'basico', label: 'Mensajes a 0,021 € (basic_message 0,021)', step: 1 },
  { key: 'total', label: 'Total € (TOTAL)', step: 0.01 }
]

/**
 * Cuadre del mes con la factura de Orange: concepto a concepto, con la diferencia, si está dentro
 * del margen y la explicación de cada diferencia con los datos del propio mes.
 */
const ReconciliationCard = ({ month, totals, rates, invoice, onSave }) => {
  const [editing, setEditing] = useState(false)
  const [draft, setDraft] = useState(null)
  const [saving, setSaving] = useState(false)

  const openEditor = () => {
    setDraft(invoice ? { ...invoice } : { conversaciones: null, simple: null, basico: null, total: null })
    setEditing(true)
  }

  const save = async () => {
    if (FIELDS.some((f) => draft[f.key] === null || draft[f.key] === undefined)) {
      message.warning('Rellena las cuatro cifras de la factura')
      return
    }
    setSaving(true)
    try {
      await onSave({ month, ...draft })
      message.success('Factura guardada')
      setEditing(false)
    } catch (err) {
      message.error('No se pudo guardar la factura')
    }
    setSaving(false)
  }

  const t = totals
  const ours = {
    conversacional: t.firstSessions,
    sesionesExtra: t.extraSessions,
    conversaciones: t.sessions,
    simple: t.simple,
    basico: t.basico,
    eventos: t.sessions + t.simple + t.basico,
    total: t.cost
  }
  const inv = invoice && {
    ...invoice,
    eventos: invoice.conversaciones + invoice.simple + invoice.basico
  }

  const rows = inv
    ? [
        // Dos líneas propias (mismo precio) para cuadrar concepto a concepto con Orange; su factura
        // solo trae el total de a2p_conversation, que se compara en la fila "Total sesiones".
        { key: 'conversacional', label: `RCS conversacional (${eur(rates.conversacional)})`, rate: rates.conversacional, detail: true },
        { key: 'sesionesExtra', label: `Sesiones extra (${eur(rates.conversacional)})`, rate: rates.conversacional, detail: true },
        {
          key: 'conversaciones',
          label: 'Total sesiones (a2p_conversation)',
          sub: `${num(t.firstSessions)} conversacional + ${num(t.extraSessions)} sesiones extra`,
          rate: rates.conversacional
        },
        { key: 'simple', label: 'Mensajes Simple (0,05 €)', rate: rates.simple },
        { key: 'basico', label: 'Mensajes Básico (0,021 €)', rate: rates.basico },
        { key: 'eventos', label: 'Total de eventos facturados' },
        { key: 'total', label: 'Total €', money: true }
      ].map((r) => {
        if (r.detail && inv[r.key] == null) return { ...r, noInvoice: true, ok: true }
        const diff = ours[r.key] - inv[r.key]
        const p = pctOf(diff, inv[r.key])
        const euros = r.money ? diff : r.rate != null ? diff * r.rate : null
        // Aceptable si está dentro del ±5 % o si su impacto en € es menor que el 1 % de la factura.
        const ok = Math.abs(p) <= TOLERANCE_PCT || (euros != null && Math.abs(euros) <= inv.total * 0.01)
        return { ...r, diff, p, euros, ok }
      })
    : []

  const d = (k) => rows.find((r) => r.key === k)
  const f = t.funnel || {}

  return (
    <Card
      size="small"
      title={`Cuadre con la factura de Orange · ${monthLabel(month)}`}
      extra={
        inv && (
          <Button size="small" icon={<EditOutlined />} onClick={openEditor}>
            Editar factura
          </Button>
        )
      }
      style={{ marginBottom: 20 }}
    >
      {!inv && (
        <Alert
          type="info"
          showIcon
          message={`Aún no está la factura de Orange de ${monthLabel(month)}`}
          description="Introduce las cuatro cifras de la hoja «RCS Agents» del Excel de Orange para ver el cuadre concepto a concepto."
          action={
            <Button type="primary" onClick={openEditor}>
              Introducir factura
            </Button>
          }
        />
      )}

      {inv && (
        <>
          <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
              <thead>
                <tr style={{ color: '#64748b', fontSize: 12 }}>
                  <th style={cellL}>Concepto</th>
                  <th style={cell}>Factura Orange</th>
                  <th style={cell}>Nuestro cálculo</th>
                  <th style={cell}>Diferencia</th>
                  <th style={cell} />
                </tr>
              </thead>
              <tbody>
                {rows.map((r) => (
                  <tr key={r.key} style={r.key === 'total' ? { fontWeight: 700 } : undefined}>
                    <td style={cellL}>
                      {r.label}
                      {r.sub && <div style={{ fontSize: 11, color: '#94a3b8', fontWeight: 400 }}>{r.sub}</div>}
                    </td>
                    <td style={cell}>
                      {r.noInvoice ? (
                        <span style={{ color: '#94a3b8' }}>en el total</span>
                      ) : r.money ? (
                        eur(inv[r.key])
                      ) : (
                        num(inv[r.key])
                      )}
                    </td>
                    <td style={cell}>{r.money ? eur(ours[r.key]) : num(ours[r.key])}</td>
                    <td style={cell}>
                      {r.noInvoice ? (
                        <span style={{ color: '#94a3b8' }}>{eur(ours[r.key] * r.rate)}</span>
                      ) : (
                      <>
                      {r.money ? signed(r.diff, eur) : signed(r.diff)} ({pctTxt(r.p)})
                      {!r.money && r.euros != null && (
                        <div style={{ fontSize: 11, color: '#94a3b8', fontWeight: 400 }}>{signed(r.euros, eur)}</div>
                      )}
                      </>
                      )}
                    </td>
                    <td style={cell}>
                      {!r.noInvoice && (
                      <Tooltip
                        title={
                          r.ok
                            ? `Dentro del margen (±${TOLERANCE_PCT} % o menos del 1 % de la factura en €)`
                            : 'Fuera del margen: revisar'
                        }
                      >
                        {r.ok ? (
                          <CheckCircleFilled style={{ color: '#16a34a' }} />
                        ) : (
                          <WarningFilled style={{ color: '#d97706' }} />
                        )}
                      </Tooltip>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <div style={{ marginTop: 14, fontSize: 13, color: '#334155' }}>
            <b>Qué explica cada cifra</b>
            <ul style={{ paddingLeft: 18, marginTop: 6, marginBottom: 0 }}>
              <li style={{ marginBottom: 6 }}>
                <b>Por qué Orange factura {num(inv.eventos)} eventos si enviamos {num(f.sent)} mensajes.</b>{' '}
                Los mensajes enviados dentro de una sesión no se cobran aparte. En el mes:{' '}
                {num(f.sent)} enviados = {num(t.simple)} Simple + {num(t.basico)} Básico +{' '}
                {num(t.openersSent)} que abren sesión + {num(t.bundled)} incluidos gratis (respuestas del bot dentro de
                una conversación ya cobrada; lo que escribe o pulsa la persona nunca se cobra).
              </li>
              <li style={{ marginBottom: 6 }}>
                <b>Sesiones ({signed(d('conversaciones').diff)}, {pctTxt(d('conversaciones').p)}).</b> Cada sesión
                la abre un clic o una respuesta del usuario y dura 24 h. Contamos{' '}
                {num(t.firstSessions)} conversaciones y {num(t.extraSessions)} sesiones extra (el mismo
                teléfono vuelve a interactuar pasadas 24 h). Orange registra las interacciones en su red;
                nosotros las deducimos de las respuestas del bot, y por eso puede haber pequeñas diferencias.
              </li>
              <li style={{ marginBottom: 6 }}>
                <b>Simple ({signed(d('simple').diff)}, {pctTxt(d('simple').p)}).</b>{' '}
                {stepList(t.byStep, 'simple') || 'Sin mensajes Simple'}. Si el usuario responde días
                después del mensaje inicial, ese mensaje ya se cobró solo, igual que hace Orange.
              </li>
              <li style={{ marginBottom: 6 }}>
                <b>Básico ({signed(d('basico').diff)}, {pctTxt(d('basico').p)}, {signed(d('basico').euros, eur)}).</b>{' '}
                Mensajes de solo texto de 160 caracteres o menos: {stepList(t.byStep, 'basico') || 'ninguno'}.
                {d('basico').diff > 0
                  ? ' Comprobado que no caen dentro de otra conversación; lo más probable es que Orange agrupe parte de los avisos repetidos a la misma persona en menos de 24 h. El impacto en € es pequeño.'
                  : d('basico').diff < 0
                  ? ' Orange cobra más mensajes de solo texto de los que vemos nosotros.'
                  : ''}
              </li>
              <li style={{ marginBottom: 6 }}>
                <b>Total ({signed(d('total').diff, eur)}, {pctTxt(d('total').p)}).</b>{' '}
                {d('total').ok
                  ? `Dentro del margen aceptado (±${TOLERANCE_PCT} %).`
                  : 'Fuera del margen aceptado: revisar tarifas y cifras de la factura.'}{' '}
                Orange cobra el mensaje aceptado aunque no llegue a entregarse: los {num(f.failed)} fallidos
                del mes están dentro del cálculo.
              </li>
              <li>
                Se compara con el total de <b>todos los clientes</b>, porque la factura de Orange incluye
                todos nuestros bots. Orange solo da totales; para atribuir cada diferencia a mensajes
                concretos haría falta su detalle mensaje a mensaje (CDR).
              </li>
            </ul>
          </div>
          <div style={{ fontSize: 11, color: '#94a3b8', marginTop: 8 }}>
            Factura introducida {invoice.updatedAt ? new Date(invoice.updatedAt).toLocaleDateString('es-ES') : ''}
            {invoice.updatedBy ? ` por ${invoice.updatedBy}` : ''}
          </div>
        </>
      )}

      <Modal
        title={`Factura de Orange · ${monthLabel(month)}`}
        open={editing}
        onOk={save}
        okText="Guardar"
        cancelText="Cancelar"
        confirmLoading={saving}
        onCancel={() => setEditing(false)}
      >
        <p style={{ fontSize: 13, color: '#64748b' }}>
          Copia las cifras de la hoja «RCS Agents» del Excel de Orange (columna Events, y Charges para
          el total).
        </p>
        {draft &&
          FIELDS.map((fl) => (
            <div key={fl.key} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12, marginBottom: 10 }}>
              <span style={{ fontSize: 13 }}>{fl.label}</span>
              <InputNumber
                min={0}
                step={fl.step}
                value={draft[fl.key]}
                onChange={(v) => setDraft({ ...draft, [fl.key]: v })}
                style={{ width: 160 }}
              />
            </div>
          ))}
      </Modal>
    </Card>
  )
}

export default ReconciliationCard
